import React from 'react';
import { Mail, Phone, MapPin, Instagram, Heart, Globe } from 'lucide-react';
import { contactData, logoUrl } from '../utils/mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const quickLinks = [
    { label: 'Home', id: 'hero' },
    { label: 'About', id: 'about' },
    { label: 'Services', id: 'services' },
    { label: 'Portfolio', id: 'portfolio' }
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white pt-16 pb-8 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <img
              src={logoUrl}
              alt="Haarmony Interiors"
              className="h-18 sm:h-20 md:h-24 w-auto max-w-[280px] mb-6 transform hover:scale-105 transition-transform duration-300"
            />
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              Transforming spaces into harmonious environments that reflect your style and enhance your lifestyle. Let's create something extraordinary together.
            </p>
            <div className="flex gap-4">
              <a
                href={`https://instagram.com/${contactData.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-rose-500 to-pink-500 rounded-full p-3 hover:shadow-lg hover:shadow-rose-500/50 transition-all duration-300 transform hover:scale-110"
              >
                <Instagram className="h-5 w-5 text-white" />
              </a>
              <a
                href={contactData.website}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-rose-500 to-pink-500 rounded-full p-3 hover:shadow-lg hover:shadow-rose-500/50 transition-all duration-300 transform hover:scale-110"
              >
                <Globe className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 hover:text-white transition-colors hover:translate-x-2 inline-block transform duration-300"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`mailto:${contactData.email}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group"
                >
                  <Mail className="h-5 w-5 mt-0.5 text-rose-500 group-hover:text-rose-400 transform group-hover:scale-110 transition-all" />
                  <span className="break-words text-sm">{contactData.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${contactData.phone}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group"
                >
                  <Phone className="h-5 w-5 mt-0.5 text-rose-500 group-hover:text-rose-400 transform group-hover:scale-110 transition-all" />
                  <span className="text-sm">{contactData.phone}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="h-5 w-5 mt-0.5 text-rose-500 flex-shrink-0" />
                <span className="text-sm">{contactData.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Haarmony Interiors. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm flex items-center gap-2">
              Made with <Heart className="h-4 w-4 text-rose-500 fill-rose-500 animate-pulse" /> for beautiful spaces
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
